// Раздел "Настройки" (docs/PLAN.md, запрос пользователя 2026-08-21) — то, что раньше было
// захардкожено в .env/Python-константах: логин/пароль админки, скидки за количество устройств,
// текст поддержки, лимит неудачных попыток промокода.
import { type FormEvent, useEffect, useState } from "react";

import {
  type AdminSettings,
  ApiError,
  changeAdminPassword,
  fetchAdminSettings,
  updateAdminSettings,
} from "../api/client";

// Скидки хранятся в API долями (0.2 = 20%) — в форме удобнее видеть целые проценты.
function toPercent(fraction: number): string {
  return String(Math.round(fraction * 100));
}

function toFraction(percent: string): number {
  return Number(percent) / 100;
}

export function SettingsScreen() {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [username, setUsername] = useState("");
  const [supportContact, setSupportContact] = useState("");
  const [discount2, setDiscount2] = useState("0");
  const [discount3, setDiscount3] = useState("0");
  const [discount4, setDiscount4] = useState("0");
  const [discount5, setDiscount5] = useState("0");
  const [maxFailedAttempts, setMaxFailedAttempts] = useState("4");
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [generalSaved, setGeneralSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);

  function applyToForm(data: AdminSettings) {
    setUsername(data.admin_username);
    setSupportContact(data.support_contact ?? "");
    setDiscount2(toPercent(data.discount_2_devices));
    setDiscount3(toPercent(data.discount_3_devices));
    setDiscount4(toPercent(data.discount_4_devices));
    setDiscount5(toPercent(data.discount_5_devices));
    setMaxFailedAttempts(String(data.promo_max_failed_attempts));
  }

  useEffect(() => {
    fetchAdminSettings()
      .then((data) => {
        setSettings(data);
        applyToForm(data);
      })
      .catch((err) => {
        setLoadError(err instanceof ApiError ? err.message : "Не удалось загрузить настройки");
      });
  }, []);

  async function onSaveGeneral(event: FormEvent) {
    event.preventDefault();
    setSavingGeneral(true);
    setGeneralError(null);
    setGeneralSaved(false);
    try {
      const updated = await updateAdminSettings({
        admin_username: username,
        support_contact: supportContact.trim() || null,
        discount_2_devices: toFraction(discount2),
        discount_3_devices: toFraction(discount3),
        discount_4_devices: toFraction(discount4),
        discount_5_devices: toFraction(discount5),
        promo_max_failed_attempts: Number(maxFailedAttempts),
      });
      setSettings(updated);
      applyToForm(updated);
      setGeneralSaved(true);
    } catch (err) {
      setGeneralError(err instanceof ApiError ? err.message : "Не удалось сохранить настройки");
    } finally {
      setSavingGeneral(false);
    }
  }

  async function onChangePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSaved(false);
    if (newPassword !== confirmPassword) {
      setPasswordError("Новый пароль и подтверждение не совпадают");
      return;
    }
    setSavingPassword(true);
    try {
      await changeAdminPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSaved(true);
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : "Не удалось сменить пароль");
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div className="screen">
      <h2>Настройки</h2>

      {loadError && <div className="error-text">{loadError}</div>}

      {settings && (
        <>
          <form onSubmit={onSaveGeneral} className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginTop: 0 }}>Общие</h3>

            <div className="field">
              <label>Логин администратора</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} required />
            </div>

            <div className="field">
              <label>Текст поддержки (ответ бота на /support)</label>
              <input
                value={supportContact}
                onChange={(e) => setSupportContact(e.target.value)}
                placeholder="например, @av_support или support@example.com"
              />
            </div>

            <div className="field">
              <label>Скидки за количество устройств, % (1 устройство — всегда без скидки)</label>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12 }}>2 устройства</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={discount2}
                    onChange={(e) => setDiscount2(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12 }}>3 устройства</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={discount3}
                    onChange={(e) => setDiscount3(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12 }}>4 устройства</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={discount4}
                    onChange={(e) => setDiscount4(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12 }}>5 устройств</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={discount5}
                    onChange={(e) => setDiscount5(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="field">
              <label>Лимит неудачных попыток ввода промокода до блокировки</label>
              <input
                type="number"
                min={1}
                max={100}
                value={maxFailedAttempts}
                onChange={(e) => setMaxFailedAttempts(e.target.value)}
                style={{ maxWidth: 120 }}
              />
            </div>

            {generalError && <div className="error-text">{generalError}</div>}
            {generalSaved && !generalError && (
              <p style={{ color: "var(--color-success)", fontSize: 14 }}>Сохранено.</p>
            )}
            <button type="submit" className="primary" disabled={savingGeneral}>
              {savingGeneral ? "Сохраняем…" : "Сохранить"}
            </button>
          </form>

          <form onSubmit={onChangePassword} className="card">
            <h3 style={{ marginTop: 0 }}>Смена пароля</h3>

            <div className="field">
              <label>Текущий пароль</label>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Новый пароль</label>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Повторите новый пароль</label>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {passwordError && <div className="error-text">{passwordError}</div>}
            {passwordSaved && !passwordError && (
              <p style={{ color: "var(--color-success)", fontSize: 14 }}>
                Пароль изменён. Он понадобится при следующем входе.
              </p>
            )}
            <button type="submit" className="primary" disabled={savingPassword}>
              {savingPassword ? "Сохраняем…" : "Сменить пароль"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
